import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

// This is the CLIENT half of the tier-entitlement rule; the SERVER half
// lives in supabase/functions/_shared/entitlement.ts (effectiveTier/
// meetsTier), used by edge functions that must enforce the same gate
// server-side. Keep the trial/cumulative-tier rules below in lockstep
// with that file.

export type SubscriptionTier = 'free' | 'pro' | 'power' | 'studio';

interface Entitlement {
  tier: SubscriptionTier;
  effectiveTier: SubscriptionTier;
  isLoading: boolean;
  isPro: boolean;
  isPower: boolean;
  isStudio: boolean;
  isTrialActive: boolean;
  trialExpiresAt: Date | null;
  /** Sprint 23: operator flag (profiles.is_admin). Gates /admin/*
   *  routes. Set via SQL — no UI toggle. */
  isAdmin: boolean;
}

const TIER_RANK: Record<SubscriptionTier, number> = {
  free: 0,
  pro: 1,
  power: 2,
  studio: 3,
};

function atLeast(tier: SubscriptionTier, min: SubscriptionTier): boolean {
  return TIER_RANK[tier] >= TIER_RANK[min];
}

interface EntitlementSnapshot {
  tier: SubscriptionTier;
  isTrialActive: boolean;
  trialExpiresAt: Date | null;
  isAdmin: boolean;
}

const FREE_SNAPSHOT: EntitlementSnapshot = {
  tier: 'free',
  isTrialActive: false,
  trialExpiresAt: null,
  isAdmin: false,
};

async function fetchEntitlement(userId: string): Promise<EntitlementSnapshot> {
  // Permissive select('*') so older deployments where is_admin
  // hasn't migrated yet still return a row — we then fall back to
  // `false` rather than throwing.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data } = await (supabase as any)
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .maybeSingle();

  const raw = data?.subscription_tier ?? 'free';
  const baseTier = raw in TIER_RANK ? (raw as SubscriptionTier) : 'free';

  const expiresAt = data?.trial_expires_at ? new Date(data.trial_expires_at) : null;
  const active = expiresAt != null && expiresAt > new Date();

  return {
    tier: baseTier,
    isTrialActive: active,
    trialExpiresAt: expiresAt,
    isAdmin: Boolean(data?.is_admin),
  };
}

export function useEntitlement(): Entitlement {
  // Resolve the signed-in user id first. Keying the entitlement query on this
  // id means a sign-in/sign-out automatically swaps caches, and a manual
  // `invalidateQueries({ queryKey: ['entitlement'] })` (e.g. after starting a
  // trial) refetches without a full page reload.
  const { data: userId, isLoading: userLoading } = useQuery({
    queryKey: ['auth-user'],
    queryFn: async () => {
      const result = await supabase.auth.getUser();
      return result?.data?.user?.id ?? null;
    },
    staleTime: 60_000,
  });

  const { data: snapshot, isLoading: entitlementLoading } = useQuery({
    queryKey: ['entitlement', userId],
    queryFn: () => fetchEntitlement(userId as string),
    enabled: !!userId,
  });

  // isLoading mirrors the old single-fetch semantics: we're loading while the
  // user is resolving, or while the (enabled) entitlement query is in flight.
  // A logged-out user (no userId) is not loading — they resolve to free.
  const isLoading = userLoading || (!!userId && entitlementLoading);

  const { tier, isTrialActive, trialExpiresAt, isAdmin } = snapshot ?? FREE_SNAPSHOT;

  // An active trial floors the effective tier at 'pro' — but it must NEVER
  // downgrade a customer who already pays for a tier above pro (power/studio).
  const effectiveTier: SubscriptionTier =
    isTrialActive && TIER_RANK[tier] < TIER_RANK['pro'] ? 'pro' : tier;

  return {
    tier,
    effectiveTier,
    isLoading,
    isPro: atLeast(effectiveTier, 'pro'),
    isPower: atLeast(effectiveTier, 'power'),
    isStudio: atLeast(effectiveTier, 'studio'),
    isTrialActive,
    trialExpiresAt,
    isAdmin,
  };
}
